import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchContact, fetchContactReports, fetchContacts } from '../api/contacts'
import { fetchChainVerify, fetchLatestCheckpoint, fetchLedgerEntries } from '../api/ledger'
import { useContactStore } from '../store/contactStore'
import { useAuth } from './useAuth'
import type { ContactFilters } from '../store/contactStore'

/** Seeds the store from the current filters. The map/list both read from the store afterward,
 * with WebSocket updates (useLiveContacts) keeping it fresh between polls. */
export function useMapContacts(filters: ContactFilters) {
  const setContacts = useContactStore((s) => s.setContacts)
  const result = useQuery({
    queryKey: ['contacts', filters],
    queryFn: () => fetchContacts(filters),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  useEffect(() => {
    if (result.data) setContacts(result.data)
  }, [result.data, setContacts])
  return result
}

export function useContactDetail(id: string | null) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: () => fetchContact(id!),
    enabled: !!id,
    staleTime: 30_000,
  })
}

/** Only fetches once the caller opts in (`enabled`) — most contact views never need the
 * full ledger history, so this shouldn't fire on every selection. */
export function useLedgerEntries(recordId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['ledger', 'contacts', recordId],
    queryFn: () => fetchLedgerEntries('contacts', recordId!),
    enabled: !!recordId && enabled,
    staleTime: 30_000,
  })
}

/** Requires login (the endpoint itself is auth-gated) — only fetches once the caller opts in,
 * same reasoning as useLedgerEntries above. */
export function useContactReports(contactId: string | null, enabled: boolean) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['contacts', contactId, 'reports'],
    queryFn: () => fetchContactReports(token!, contactId!),
    enabled: !!contactId && enabled && !!token,
    staleTime: 15_000,
  })
}

export function useChainVerify() {
  return useQuery({
    queryKey: ['ledger', 'chain', 'verify'],
    queryFn: fetchChainVerify,
    staleTime: 30_000,
  })
}

export function useLatestCheckpoint() {
  return useQuery({
    queryKey: ['ledger', 'checkpoints', 'latest'],
    queryFn: fetchLatestCheckpoint,
    staleTime: 30_000,
  })
}
